import { Box, Divider, Typography } from '@mui/material';
import React, { FC } from 'react';
import { useAppSelector } from 'src/store/hooks';
import colors from 'src/styles/equalution/colors';
import { ActionButton, TotalRow, AddonRow } from './OrderSummaryShared';
import { formatIntervalDisplay, getCurrencySymbol } from 'src/lib/utils';
import { EffectivePlanObjType } from '../../types';
import { formatPricingAmount } from 'src/pages/subscription-new/utils/plans.util';
import { ORDER_SUMMARY_LINE_ITEM_WIDTH } from '../../constants';
import { useCampaign } from 'src/hooks/useCampaign';
import MoneybackGuarantee from 'src/pages/subscription-new/components/MoneybackGuarantee';

export const getTodayBillingSummary = ({
  billingAmount,
  isNutritionCoachingAdded,
  isSupplementAdded,
  nutritionCoachingPrice = 0,
  supplementPrice = 0,
  showFreeTrial = false,
  isGiftActive = false,
}: {
  billingAmount: string | number;
  isNutritionCoachingAdded?: boolean;
  isSupplementAdded?: boolean;
  nutritionCoachingPrice?: number;
  supplementPrice?: number;
  showFreeTrial?: boolean;
  isGiftActive?: boolean;
}) => {
  const effectiveSupplementPrice = isGiftActive ? 0 : supplementPrice;
  const addonTotal =
    (isNutritionCoachingAdded ? nutritionCoachingPrice : 0) +
    (isSupplementAdded ? effectiveSupplementPrice : 0);
  const total = (showFreeTrial ? 0 : Number(billingAmount)) + addonTotal;

  return {
    total,
    totalBillingLabel:
      showFreeTrial && addonTotal > 0 ? 'add-ons billed today' : 'billed today',
    priceCaption: showFreeTrial ? 'after trial' : undefined,
    effectiveSupplementPrice,
  };
};

interface NormalSignupOrderSummaryProps {
  effectivePlanObj: EffectivePlanObjType;
  isNutritionCoachingAdded?: boolean;
  isSupplementAdded?: boolean;
  nutritionCoachingPrice?: number;
  supplementPrice?: number;
  onRemoveNutritionCoaching: () => void;
  onRemoveSupplement: () => void;
  handleChangePlan: () => void;
  description?: string;
  originalBillingAmount?: string;
  // Override for the plan title (default: "{intervalCount} Month Subscription").
  // Used by skipNextCycle promos to surface the bonus cycle in the title,
  // e.g. "3 Month + 3 Free Months Subscription".
  displayPlanName?: string;
  // Override for the per-cycle suffix shown under the price (default: the
  // formatted interval like "3 months"). Used by skipNextCycle promos to show
  // the effective committed length, e.g. "6 months".
  displayIntervalLabel?: string;
  showFreeTrial?: boolean;
  // Gift-with-purchase: supplement is free ($0 with strikethrough on original).
  isGiftActive?: boolean;
  // Campaign-specific label shown below the plan name (e.g. "+ 10 weeks free").
  campaignLabel?: string;
  useOutlineButton?: boolean;
  // Dark pill badge shown above the plan row (e.g. "1 Month Free").
  badgeLabel?: string;
  // Overrides effectivePlanObj.unitAmount as the displayed plan price.
  // Used by DISCOUNT_ROLLOVER to show the discounted price while the plan object holds the full Stripe price.
  billingAmountOverride?: string | number;
}

const NormalSignupOrderSummary: FC<NormalSignupOrderSummaryProps> = ({
  effectivePlanObj,
  isNutritionCoachingAdded,
  isSupplementAdded,
  nutritionCoachingPrice = 0,
  supplementPrice = 0,
  onRemoveNutritionCoaching,
  onRemoveSupplement,
  handleChangePlan,
  description,
  originalBillingAmount,
  displayPlanName,
  displayIntervalLabel,
  showFreeTrial = false,
  isGiftActive = false,
  campaignLabel,
  useOutlineButton = false,
  badgeLabel,
  billingAmountOverride,
}) => {
  const { isPaymentProcessing } = useAppSelector(state => state.payment);
  const { is10BDayCampaign } = useCampaign();

  // Calculate values from plan object
  const billingAmount = billingAmountOverride ?? effectivePlanObj.unitAmount;
  const intervalCount = effectivePlanObj.intervalCount;
  const interval = effectivePlanObj.interval;
  const intervalString =
    displayIntervalLabel ?? formatIntervalDisplay(intervalCount, interval);
  const planDisplayName =
    displayPlanName ?? `${intervalCount} Month Subscription`;

  const { total, totalBillingLabel, priceCaption, effectiveSupplementPrice } =
    getTodayBillingSummary({
      billingAmount,
      isNutritionCoachingAdded,
      isSupplementAdded,
      nutritionCoachingPrice,
      supplementPrice,
      showFreeTrial,
      isGiftActive,
    });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
      }}>
      <Box
        sx={{
          backgroundColor: colors.OFF_WHITE,
          padding: '24px 16px',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}>
        {badgeLabel && (
          <Box
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: colors.OFF_BLACK,
              color: colors.OFF_WHITE,
              borderRadius: '100px',
              padding: '1px 12px',
              fontSize: '12px',
              fontWeight: 400,
            }}>
            {badgeLabel}
          </Box>
        )}
        <MainPlanRow
          isLoading={isPaymentProcessing}
          planName={planDisplayName}
          billingAmount={billingAmount}
          originalBillingAmount={originalBillingAmount}
          intervalString={intervalString}
          priceCaption={priceCaption}
          onChangePlan={handleChangePlan}
          campaignLabel={campaignLabel}
          useOutlineButton={useOutlineButton}
        />

        {isNutritionCoachingAdded && (
          <AddonRow
            label="Coaching Add-On"
            price={nutritionCoachingPrice}
            priceFormat="/month"
            isLoading={isPaymentProcessing}
            onRemove={onRemoveNutritionCoaching}
          />
        )}

        {isSupplementAdded && (
          <AddonRow
            label="Supplement Pack"
            price={effectiveSupplementPrice}
            originalPrice={isGiftActive ? supplementPrice : undefined}
            priceFormat="one off"
            isLoading={isPaymentProcessing}
            onRemove={onRemoveSupplement}
            hideRemove={isGiftActive}
          />
        )}

        <Divider sx={{ width: '100%', my: 0.5 }} />

        <TotalRow total={total} billingLabel={totalBillingLabel} />

        {description && (
          <Typography
            sx={{
              mt: 1,
              fontSize: '14px',
              color: colors.OFF_BLACK,
              textAlign: 'left',
              width: '100%',
            }}>
            {description}
          </Typography>
        )}

        {is10BDayCampaign && (
          <MoneybackGuarantee style={{ marginTop: '4px' }} />
        )}
      </Box>
    </Box>
  );
};

export default NormalSignupOrderSummary;

const MainPlanRow = ({
  planName,
  billingAmount,
  originalBillingAmount,
  intervalString,
  priceCaption,
  onChangePlan,
  isLoading,
  campaignLabel,
  useOutlineButton,
}: {
  planName: string;
  billingAmount: string | number;
  originalBillingAmount?: string;
  intervalString: string;
  priceCaption?: string;
  onChangePlan: () => void;
  isLoading: boolean;
  campaignLabel?: string;
  useOutlineButton?: boolean;
}) => {
  const savings = originalBillingAmount
    ? Number(originalBillingAmount) - Number(billingAmount)
    : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
      }}>
      <Box
        sx={{
          width: ORDER_SUMMARY_LINE_ITEM_WIDTH,
          flex: 'none',
          paddingRight: '16px',
        }}>
        <Typography
          sx={{
            fontSize: '16px',
            color: colors.OFF_BLACK,
            fontWeight: 400,
          }}>
          {planName}
        </Typography>
        {campaignLabel && (
          <Typography
            sx={{
              fontSize: '14px',
              color: colors.SALAD,
              fontWeight: 600,
              marginTop: '4px',
            }}>
            {campaignLabel}
          </Typography>
        )}
        {savings > 0 && (
          <Typography
            sx={{
              fontSize: '12px',
              color: colors.SALAD,
              fontWeight: 600,
              marginTop: '4px',
            }}>
            You've saved ${formatPricingAmount(savings)}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              color: colors.OFF_BLACK,
              whiteSpace: 'nowrap',
            }}>
            {getCurrencySymbol() + formatPricingAmount(billingAmount)}
          </Typography>
          {originalBillingAmount &&
            Number(originalBillingAmount) !== Number(billingAmount) && (
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: colors.OFF_BLACK,
                  textDecoration: 'line-through',
                  whiteSpace: 'nowrap',
                }}>
                {getCurrencySymbol() +
                  formatPricingAmount(originalBillingAmount)}
              </Typography>
            )}
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: colors.OFF_BLACK,
              marginTop: '-2px',
              whiteSpace: 'nowrap',
            }}>
            {priceCaption ?? `/${intervalString}`}
          </Typography>
        </Box>
        <ActionButton
          isLoading={isLoading}
          label="Change"
          onClick={onChangePlan}
          useOutlineButton={useOutlineButton}
        />
      </Box>
    </Box>
  );
};
